import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { Icons } from '@texas/components/shared/Icons';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { ServerError } from '@texas/types';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TierRow {
  id: string;
  quantity: number | '';
}

interface Props extends SharedDisclosureProps {
  title: string;
  existingQuantities: number[];
  recommendedQuantities: number[];
  loadingRecommendations?: boolean;
  submitting: boolean;
  submitError?: ServerError | null;
  onSubmit: (quantities: number[]) => Promise<boolean>;
}

const MAX_ROWS = 10;

export function AddMultipleTiersModal({
  title,
  existingQuantities,
  recommendedQuantities,
  loadingRecommendations,
  submitting,
  submitError,
  isOpen,
  onClose,
  onSubmit,
}: Props) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      {isOpen && (
        <AddMultipleTiersModalInner
          title={title}
          existingQuantities={existingQuantities}
          recommendedQuantities={recommendedQuantities}
          loadingRecommendations={loadingRecommendations}
          submitting={submitting}
          submitError={submitError}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      )}
    </Modal>
  );
}

function AddMultipleTiersModalInner({
  title,
  existingQuantities,
  recommendedQuantities,
  loadingRecommendations,
  submitting,
  submitError,
  onClose,
  onSubmit,
}: Omit<Props, 'isOpen'>) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<TierRow[]>([
    { id: crypto.randomUUID(), quantity: '' },
  ]);
  const [formError, setFormError] = useState<string>('');
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  const addedQuantities = useMemo(() => {
    return rows.map((r) => r.quantity).filter((q) => q !== '') as number[];
  }, [rows]);

  const availableRecommendedQuantities = useMemo(() => {
    return recommendedQuantities.filter(
      (quantity) =>
        !existingQuantities.includes(quantity) &&
        !addedQuantities.includes(quantity),
    );
  }, [recommendedQuantities, existingQuantities, addedQuantities]);

  const addRow = () => {
    if (rows.length < MAX_ROWS) {
      const newRow = { id: crypto.randomUUID(), quantity: '' as const };
      setRows([...rows, newRow]);
      return newRow.id;
    }
    return null;
  };

  const removeRow = (id: string) => {
    if (rows.length > 1) {
      setRows(rows.filter((row) => row.id !== id));
      inputRefs.current.delete(id);
    }
  };

  const updateQuantity = (id: string, value: string) => {
    const quantity = value === '' ? '' : parseInt(value, 10);
    setRows(rows.map((row) => (row.id === id ? { ...row, quantity } : row)));
    setFormError('');
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowId: string,
    currentValue: number | '',
  ) => {
    if (e.key === 'Enter' && currentValue !== '' && rows.length < MAX_ROWS) {
      e.preventDefault();
      const newRowId = addRow();
      if (newRowId) {
        setTimeout(() => {
          inputRefs.current.get(newRowId)?.focus();
        }, 0);
      }
    }
  };

  const addRecommendedTier = (quantity: number) => {
    const exists = rows.some((r) => r.quantity === quantity);
    if (exists) return;

    const emptyRowIndex = rows.findIndex((r) => r.quantity === '');
    if (emptyRowIndex >= 0) {
      setRows(
        rows.map((row, idx) =>
          idx === emptyRowIndex ? { ...row, quantity } : row,
        ),
      );
    } else if (rows.length < MAX_ROWS) {
      setRows([...rows, { id: crypto.randomUUID(), quantity }]);
    }
  };

  const submit = async () => {
    setFormError('');

    const filledQuantities = rows
      .map((r) => r.quantity)
      .filter((q) => q !== '' && q > 0) as number[];

    if (filledQuantities.length === 0) {
      setFormError(t('tiers.atLeastOneRequired'));
      return;
    }

    const uniqueQuantities = [...new Set(filledQuantities)];
    if (uniqueQuantities.length !== filledQuantities.length) {
      setFormError(t('tiers.duplicateQuantities'));
      return;
    }

    if (
      filledQuantities.some((quantity) => existingQuantities.includes(quantity))
    ) {
      setFormError(t('tiers.duplicateQuantities'));
      return;
    }

    const response = await onSubmit(uniqueQuantities);
    if (response) onClose();
  };

  return (
    <ModalContent>
      <ModalCloseButton />
      <ModalHeader>{title}</ModalHeader>
      {submitError && <ErrorDetails error={submitError} />}
      <ModalBody>
        <Box mb={6}>
          <Text variant="header-small" mb={2}>
            {t('tiers.recommended')}
          </Text>
          <Flex gap={2} flexWrap="wrap">
            {availableRecommendedQuantities.length === 0 &&
              !loadingRecommendations && (
                <Text variant="sub">
                  {t('tiers.targetTiers.noRecommendationsFound')}
                </Text>
              )}
            {availableRecommendedQuantities.map((quantity) => {
              return (
                <Button
                  key={quantity}
                  size="sm"
                  variant="texas-outline-light"
                  onClick={() => addRecommendedTier(quantity)}
                >
                  {t('general.quantityCount', { count: quantity })}
                </Button>
              );
            })}
          </Flex>
        </Box>

        <Box>
          <Table variant="unstyled" size="sm">
            <Thead>
              <Tr>
                <Th>{t('general.quantity')}</Th>
                <Th width="60px">{t('general.actions')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id}>
                  <Td>
                    <Input
                      ref={(el) => {
                        if (el) {
                          inputRefs.current.set(row.id, el);
                        } else {
                          inputRefs.current.delete(row.id);
                        }
                      }}
                      variant="texas"
                      type="number"
                      value={row.quantity}
                      onChange={(e) => updateQuantity(row.id, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, row.id, row.quantity)}
                      min={1}
                      placeholder={t('general.quantity')}
                    />
                  </Td>
                  <Td>
                    <IconButton
                      aria-label="Remove row"
                      icon={<Icons.Close />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => removeRow(row.id)}
                      isDisabled={rows.length === 1}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          <Button
            leftIcon={<Icons.Plus />}
            size="sm"
            variant="ghost"
            onClick={addRow}
            isDisabled={rows.length >= MAX_ROWS}
            mt={2}
          >
            {t('tiers.addRow')}
            {rows.length >= MAX_ROWS && <> ({t('tiers.maxRows')})</>}
          </Button>
          {formError && (
            <Text color="red.500" fontSize="sm" mt={2}>
              {formError}
            </Text>
          )}
        </Box>
      </ModalBody>

      <ModalFooter>
        <Button variant="ghost" mr={3} onClick={onClose}>
          {t('general.cancel')}
        </Button>
        <Button isLoading={submitting} disabled={submitting} onClick={submit}>
          {t('tiers.addTiers')}
        </Button>
      </ModalFooter>
    </ModalContent>
  );
}
